// ============================================================
// ByOurConsent.com -- Chapters Page
// /chapters
// ============================================================

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Nav    from '../components/Nav';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import api    from '../utils/api';

const ChaptersPage = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [chapters,   setChapters]   = useState([]);
    const [loading,    setLoading]    = useState(true);
    const [activeType, setActiveType] = useState('all');
    const [search,     setSearch]     = useState('');

    const handleStartChapter = () => {
        if (user) navigate('/chapters/apply');
        else navigate('/login', { state: { from: { pathname: '/chapters/apply' } } });
    };

    useEffect(() => {
        const params = new URLSearchParams();
        if (activeType !== 'all') params.set('type', activeType);
        if (search) params.set('search', search);
        setLoading(true);
        api.get(`/chapters?${params}`)
            .then(r => setChapters(r.data.chapters || []))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [activeType, search]);

    const TypeBadge = ({ type }) => (
        <span style={{
            fontSize: '9px', fontFamily: 'sans-serif',
            letterSpacing: '0.12em', textTransform: 'uppercase',
            background: type === 'location' ? '#e8f0ff' : '#f0e8ff',
            color:      type === 'location' ? '#2a4a8a' : '#4a2a8a',
            padding: '2px 7px', borderRadius: '20px', fontWeight: 600
        }}>
            {type === 'location' ? '📍 Location' : '💡 Concept'}
        </span>
    );

    return (
        <>
            <Helmet>
                <title>Chapters — By Our Consent</title>
                <meta name="description" content="Join or start a By Our Consent chapter in your area or around a shared cause." />
            </Helmet>

            <Nav />

            {/* Hero */}
            <div style={{ background: '#1e2a3a', borderBottom: '2px solid #8b1a1a', padding: '2.5rem 1.5rem' }}>
                <div style={{ maxWidth: '860px', margin: '0 auto' }}>
                    <div style={{ fontSize: '11px', fontFamily: 'sans-serif', letterSpacing: '0.14em', textTransform: 'uppercase', color: '#8b9a7a', marginBottom: '10px' }}>
                        Community
                    </div>
                    <h1 style={{ fontSize: '36px', fontWeight: 600, color: '#e8dfc8', fontFamily: "'EB Garamond', Georgia, serif", lineHeight: 1.15, marginBottom: '10px' }}>
                        Chapters
                    </h1>
                    <p style={{ fontSize: '15px', fontFamily: 'sans-serif', color: '#8a9aaa', lineHeight: 1.6, maxWidth: '560px', marginBottom: '1.5rem' }}>
                        Local groups and issue-focused communities working together for constitutional governance and accountability.
                    </p>
                    <button onClick={handleStartChapter}
                        style={{ background: '#8b1a1a', color: '#f5f0e8', border: 'none', padding: '10px 24px', fontSize: '12px', fontFamily: 'sans-serif', letterSpacing: '0.08em', textTransform: 'uppercase', cursor: 'pointer', borderRadius: '3px' }}>
                        Start a chapter →
                    </button>
                </div>
            </div>

            {/* Filter bar */}
            <div style={{ background: '#ede8dc', borderBottom: '0.5px solid #c8beaa', padding: '10px 1.5rem' }}>
                <div style={{ maxWidth: '860px', margin: '0 auto', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                    {['all','location','concept'].map(t => (
                        <button key={t} onClick={() => setActiveType(t)}
                            style={{ background: activeType === t ? '#1e2a3a' : 'transparent', color: activeType === t ? '#e8dfc8' : '#4a5060', border: `0.5px solid ${activeType === t ? '#1e2a3a' : '#c8beaa'}`, padding: '4px 14px', fontSize: '11px', fontFamily: 'sans-serif', borderRadius: '20px', cursor: 'pointer', textTransform: 'capitalize' }}>
                            {t === 'all' ? 'All chapters' : t === 'location' ? '📍 Location' : '💡 Concept'}
                        </button>
                    ))}
                    <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                        placeholder="Search chapters..."
                        style={{ marginLeft: 'auto', background: '#f5f0e8', border: '0.5px solid #c8beaa', padding: '6px 12px', fontSize: '12px', fontFamily: 'sans-serif', color: '#1e2a3a', borderRadius: '3px', outline: 'none', width: '200px' }} />
                </div>
            </div>

            {/* Chapters list */}
            <div style={{ maxWidth: '860px', margin: '0 auto', padding: '2rem 1.5rem' }}>
                {loading ? (
                    <div style={{ textAlign: 'center', padding: '3rem', fontFamily: 'sans-serif', color: '#8a8070' }}>Loading...</div>
                ) : chapters.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '3rem', background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '4px' }}>
                        <div style={{ fontSize: '32px', marginBottom: '10px' }}>🏛️</div>
                        <div style={{ fontSize: '18px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif", marginBottom: '6px' }}>
                            No chapters yet
                        </div>
                        <div style={{ fontSize: '13px', fontFamily: 'sans-serif', color: '#8a8070', marginBottom: '1rem' }}>
                            Be the first to start one!
                        </div>
                        <button onClick={handleStartChapter}
                            style={{ background: '#8b1a1a', color: '#f5f0e8', border:'none', padding: '8px 20px', fontSize: '12px', fontFamily: 'sans-serif', cursor:'pointer', borderRadius: '3px', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                            Apply to start a chapter
                        </button>
                    </div>
                ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: '1rem' }}>
                        {chapters.map(c => (
                            <Link key={c.id} to={`/chapters/${c.slug}`} style={{ textDecoration: 'none' }}>
                                <div style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '4px', padding: '1.25rem', borderLeft: '3px solid #8b1a1a', transition: 'border-color 0.15s' }}
                                    onMouseEnter={e => e.currentTarget.style.borderLeftColor = '#1e2a3a'}
                                    onMouseLeave={e => e.currentTarget.style.borderLeftColor = '#8b1a1a'}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                                        <div style={{ fontSize: '17px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif", lineHeight: 1.2 }}>
                                            {c.name}
                                        </div>
                                        <TypeBadge type={c.type} />
                                    </div>
                                    {c.location && (
                                        <div style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#8b6a3a', marginBottom: '6px' }}>
                                            📍 {c.location}
                                        </div>
                                    )}
                                    {c.description && (
                                        <div style={{ fontSize: '13px', fontFamily: 'sans-serif', color: '#4a5060', lineHeight: 1.5, marginBottom: '10px' }}>
                                            {c.description.length > 120 ? c.description.slice(0, 120) + '...' : c.description}
                                        </div>
                                    )}
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <div style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#8a8070' }}>
                                            {c.member_count > 0 ? `${c.member_count} members` : 'New chapter'}
                                            {c.leader_first && ` · Led by ${c.leader_first} ${c.leader_last}`}
                                        </div>
                                        <span style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#8b1a1a' }}>View →</span>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}

                {/* Start a chapter CTA */}
                <div style={{ marginTop: '3rem', padding: '1.5rem', background: '#1e2a3a', borderRadius: '4px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                    <div>
                        <div style={{ fontSize: '18px', fontWeight: 600, color: '#e8dfc8', fontFamily: "'EB Garamond', Georgia, serif", marginBottom: '4px' }}>
                            Don't see your area or cause?
                        </div>
                        <div style={{ fontSize: '13px', fontFamily: 'sans-serif', color: '#8a9aaa' }}>
                            Apply to start a chapter — location based or concept based.
                        </div>
                    </div>
                    <button onClick={handleStartChapter}
                        style={{ background: '#8b1a1a', color: '#f5f0e8', border:'none', padding: '10px 24px', fontSize: '12px', fontFamily: 'sans-serif', letterSpacing: '0.08em', textTransform: 'uppercase', cursor:'pointer', borderRadius: '3px', flexShrink: 0 }}>
                        Start a chapter →
                    </button>
                </div>
            </div>

            <Footer />
        </>
    );
};

export default ChaptersPage;
